import { Stack } from '@mui/material'
import { RegModalWrapper } from '../ModalContent'
import styles from '../index.module.scss'
import { useMemo, useState } from 'react'
import pxToRem from '../../../../helpers/pxToRem'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { UserRegSx } from '../../../../components/TextField'
import FileUpload from '../../../../components/FileUpload'
import CountrySelect from '../../../../components/AutoComplete/CountryInput'
import { documentUploadHandler, secondaryIdentity } from './helper'
import { FileType } from '../../../../components/FileUpload'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import {
    selectTeacherPrimaryIdentity,
    selectTeacherSecondaryIdentity,
    selectWorkingRightsType,
} from '../../../../redux/teacherSlice'
import { identityFormSchema, IdentityFormType } from './PrimaryIdentity'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { Controller, useForm } from 'react-hook-form'
import {
    IdentificationDocument,
    WorkingRightsType,
} from '../../../../schemas/teacherReg'
import { selectUserUid } from '../../../../redux/userSlice'
import { zodResolver } from '@hookform/resolvers/zod'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import { push, ref, update } from 'firebase/database'
import { db } from '../../../../firebase/configs'

export const SecondaryIdentityWrapper = () => {
    const primaryDoc = useSelector(selectTeacherPrimaryIdentity)
    const secondaryDoc = useSelector(selectTeacherSecondaryIdentity)
    const workingRightsType = useSelector(selectWorkingRightsType)

    const updatedSchema = useMemo(
        () =>
            primaryDoc.loading
                ? undefined
                : identityFormSchema.check((ctx) => {
                      if (
                          typeof primaryDoc.document.data?.birthDate ===
                              'string' &&
                          ctx.value.birthDate.format('DD-MM-YYYY') !==
                              primaryDoc.document.data.birthDate
                      ) {
                          ctx.issues.push({
                              code: 'custom',
                              message:
                                  'This birthdate does not match the other identification document',
                              input: ctx.value.birthDate,
                              path: ['birthDate'],
                          })
                      }
                  }),
        [primaryDoc]
    )

    if (
        primaryDoc.loading ||
        workingRightsType.loading ||
        secondaryDoc.loading ||
        updatedSchema === undefined
    )
        return <LoadingSpinner />

    return (
        <SecondaryIdentityForm
            id={secondaryDoc.document.id}
            primaryDoc={primaryDoc.document.data}
            secondaryDoc={secondaryDoc.document.data}
            workingRightsType={workingRightsType.data}
            schema={updatedSchema}
        />
    )
}

const SecondaryIdentityForm = ({
    id,
    workingRightsType,
    primaryDoc,
    secondaryDoc: data,
    schema,
}: {
    id: string | undefined
    primaryDoc: IdentificationDocument
    secondaryDoc: IdentificationDocument
    workingRightsType: WorkingRightsType | null
    schema: typeof identityFormSchema
}) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        formState: { isDirty, isValid },
        setValue,
        control,
        handleSubmit,
        resetField,
        watch,
    } = useForm<IdentityFormType>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            documentType: data.documentType || undefined,
            country: data.country || '',
            name: data.name || '',
            reference: data.reference || '',
            birthDate: data.birthDate
                ? dayjs(data.birthDate, 'DD-MM-YYYY')
                : undefined,
            document: data.value || undefined,
        },
    })
    const selectedDocType = watch('documentType')

    const [file, setFile] = useState<FileType | undefined>(
        typeof data.value === 'string' ? { file: data.value } : undefined
    )
    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })

        setFile({ file: file, fileName: file?.name })
        setValue('document', f, {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    const handleUpdate = async (submission: IdentityFormType) => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }
            if (!isDirty) return

            const { fileUrl, fileType, docTitle } = await documentUploadHandler(
                userId,
                file,
                {
                    url: data.value,
                    type: data.type,
                    docTitle: data.docTitle,
                }
            ) // TODO: Add type and name here

            let toUpdate = {
                ...submission,

                // Do not push File object into update
                document: null,
                value: fileUrl,
                type: fileType,
                docTitle: docTitle,

                birthDate: submission.birthDate.format('DD-MM-YYYY'),
                state: 'submitted',
            }

            if (typeof id === 'string') {
                // Update data
                await update(
                    ref(db, `teachers/${userId}/identity/${id}`),
                    toUpdate
                )
            } else {
                await push(ref(db, `teachers/${userId}/identity`), toUpdate)
            }
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    const removeFile = () => {
        setFile(undefined)
        resetField('document')
    }

    const options = useMemo(() => {
        return secondaryIdentity
            .filter((el) => {
                if (
                    workingRightsType === 'temporaryVisaHolder' &&
                    el.value === 'passport'
                ) {
                    return false
                }
                return el.value !== primaryDoc.documentType
            })
            .sort((a, b) => a.value.localeCompare(b.value))
    }, [workingRightsType, primaryDoc.documentType])

    return (
        <>
            <RegModalWrapper
                title="Identity Verification - Secondary Document"
                handleUpdate={handleSubmit(handleUpdate)}
                state={data.state}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(40)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Secondary Identification</h5>
                        <p className={styles.p}>
                            To identify your identity and working rights you
                            will need to upload two forms of identification
                        </p>
                    </Stack>
                    <Stack gap={pxToRem(24)}>
                        <FormSelect
                            control={control}
                            name="documentType"
                            options={options}
                            controlProps={{
                                sx: UserRegSx,
                            }}
                            props={{
                                label: 'Document Type',
                            }}
                        />

                        {selectedDocType !== undefined ? (
                            <>
                                <Controller
                                    render={(props) => (
                                        <CountrySelect
                                            value={props.field.value}
                                            setValue={props.field.onChange}
                                            error={!!props.fieldState.error}
                                            helperText={
                                                props.fieldState.error?.message
                                            }
                                        />
                                    )}
                                    name={'country'}
                                    control={control}
                                />

                                <FormInputText
                                    control={control}
                                    name="name"
                                    label="Name on Document"
                                    sx={UserRegSx}
                                />

                                <Stack direction={'row'} gap={pxToRem(24)}>
                                    <FormDatePicker_v2
                                        control={control}
                                        name="birthDate"
                                        datePickerProps={{
                                            format: 'DD-MM-YYYY',
                                            maxDate: dayjs(),
                                            sx: UserRegSx,
                                        }}
                                    />
                                    <FormInputText
                                        control={control}
                                        name="reference"
                                        label="Document Number"
                                        sx={UserRegSx}
                                    />
                                </Stack>

                                <RegFileUploadLabel
                                    file={file}
                                    reset={removeFile}
                                />
                                <FileUpload
                                    file={file}
                                    onChangeCallback={onFileChange}
                                    extensions={'document'}
                                    iconColor={styles.primaryYellow500}
                                />
                            </>
                        ) : null}
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export default SecondaryIdentityWrapper
